import { useState, FormEvent } from "react";
import { useStoreSettings, DAY_ORDER, DAY_LABELS } from "@/hooks/useStoreSettings";
import { useSectionBg } from "@/hooks/useSectionBg";

function formatTime12(t: string): string {
  const [h, m] = t.split(":").map(Number);
  const period = h < 12 ? "ص" : "م";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

function getTodayKey(): typeof DAY_ORDER[number] {
  const jsDay = new Date().getDay(); // 0=Sun,1=Mon,...,6=Sat
  const map: Record<number, typeof DAY_ORDER[number]> = {
    0: "sunday", 1: "monday", 2: "tuesday",
    3: "wednesday", 4: "thursday", 5: "friday", 6: "saturday",
  };
  return map[jsDay];
}

function isCurrentlyOpen(businessHours: ReturnType<typeof useStoreSettings>["settings"]["businessHours"]): boolean {
  const todayKey = getTodayKey();
  const entry = businessHours[todayKey];
  if (!entry || entry.closed) return false;
  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const [oh, om] = entry.open.split(":").map(Number);
  const [ch, cm] = entry.close.split(":").map(Number);
  return nowMins >= oh * 60 + om && nowMins < ch * 60 + cm;
}

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);
  const { settings } = useStoreSettings();
  const { bg: sectionBg } = useSectionBg('sectionContact');
  const todayKey = getTodayKey();
  const todayEntry = settings.businessHours?.[todayKey];
  const isOpen = isCurrentlyOpen(settings.businessHours ?? {});

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const msg = (form.elements.namedItem("message") as HTMLTextAreaElement).value;
    if (msg.length > 500) return;

    setStatus("sending");
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      await fetch("https://readdy.ai/api/form/d769goflpdqvlupfprc0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setStatus("success");
      form.reset();
      setCharCount(0);
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="py-20 px-4" style={{ background: sectionBg }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-amber-400 text-sm tracking-widest uppercase mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
            نحن هنا لمساعدتك
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4" style={{ fontFamily: "'Cairo', sans-serif" }}>
            إتصل بنا
          </h2>
          <div className="w-16 h-1 bg-amber-400 mx-auto rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info */}
          <div className="text-right">
            <h3 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: "'Cairo', sans-serif" }}>
              تواصل مع متجر الإبداع
            </h3>
            <p className="text-gray-400 mb-10 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
              فريقنا المتخصص جاهز لمساعدتك في اختيار أفضل المنتجات، الإجابة على استفساراتك، وتلبية طلباتك بأسرع وقت ممكن.
            </p>

            <div className="flex flex-col gap-6">
              {[
                { icon: "ri-phone-fill", label: "اتصل بنا", value: "+966 54 113 8847" },
                { icon: "ri-whatsapp-fill", label: "واتساب", value: "+966 54 113 8847" },
                { icon: "ri-mail-fill", label: "البريد الإلكتروني", value: "info@alebdaa.store" },
                { icon: "ri-map-pin-fill", label: "الموقع", value: "شارع الرئيسي 25، النزهة، الطائف 26311" },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-4 flex-row-reverse">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 border border-amber-400/20 rounded-lg text-amber-400">
                    <i className={`${c.icon} text-lg`}></i>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-500 text-xs mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>{c.label}</p>
                    <p className="text-white font-medium text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>{c.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Business Hours Card ── */}
            {settings.businessHours && (
              <div className="mt-8 bg-[#141414] border border-white/8 rounded-xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line text-amber-400 text-sm"></i>
                    <span className="text-white text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      ساعات العمل
                    </span>
                  </div>
                  {/* Today open/closed badge */}
                  <div className={`flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${
                    isOpen
                      ? "bg-green-500/15 text-green-400 border border-green-500/25"
                      : "bg-red-500/15 text-red-400 border border-red-500/25"
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? "bg-green-400 animate-pulse" : "bg-red-400"}`}></span>
                    <span style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {isOpen
                        ? `مفتوح الآن — يغلق ${todayEntry && !todayEntry.closed ? formatTime12(todayEntry.close) : ""}`
                        : todayEntry?.closed
                        ? "مغلق اليوم"
                        : `مغلق الآن — يفتح ${todayEntry ? formatTime12(todayEntry.open) : ""}`}
                    </span>
                  </div>
                </div>

                {/* Days list */}
                <div className="divide-y divide-white/5">
                  {DAY_ORDER.map((day) => {
                    const entry = settings.businessHours[day];
                    const isToday = day === todayKey;
                    return (
                      <div
                        key={day}
                        className={`flex items-center justify-between px-4 py-2.5 ${
                          isToday ? "bg-amber-400/5" : ""
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {isToday && (
                            <span className="w-1 h-1 rounded-full bg-amber-400 flex-shrink-0"></span>
                          )}
                          <span
                            className={`text-xs ${isToday ? "text-amber-400 font-bold" : "text-gray-400"}`}
                            style={{ fontFamily: "'Cairo', sans-serif" }}
                          >
                            {DAY_LABELS[day]}
                            {isToday && " (اليوم)"}
                          </span>
                        </div>
                        {entry.closed ? (
                          <span className="text-xs text-red-400/80 font-medium" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            مغلق
                          </span>
                        ) : (
                          <span
                            className={`text-xs ${isToday ? "text-amber-400 font-bold" : "text-gray-500"}`}
                            style={{ fontFamily: "'Cairo', sans-serif" }}
                            dir="rtl"
                          >
                            {formatTime12(entry.open)} – {formatTime12(entry.close)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Google Map */}
            <div className="mt-8 rounded-xl overflow-hidden border border-white/10">
              <div className="bg-[#141414] px-4 py-3 flex items-center gap-2 border-b border-white/5">
                <i className="ri-map-pin-2-fill text-amber-400 text-sm"></i>
                <span className="text-gray-300 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>موقعنا على الخريطة</span>
                <a
                  href="https://maps.app.goo.gl/E5Cqt2ggrmYcHYsn9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mr-auto flex items-center gap-1 text-amber-400 hover:text-amber-300 text-xs transition-colors cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  <i className="ri-external-link-line text-xs"></i>
                  فتح في قوقل ماب
                </a>
              </div>
              <iframe
                title="موقع متجر الإبداع على خرائط قوقل"
                src="https://maps.google.com/maps?q=al-Ebdaa+%D8%A7%D9%84%D8%A7%D8%A8%D8%AF%D8%A7%D8%B9+%D9%84%D9%84%D8%A7%D9%84%D9%83%D8%AA%D8%B1%D9%88%D9%86%D9%8A%D8%A7%D8%AA+%D9%88+%D8%A7%D9%84%D8%AD%D8%A7%D8%B3%D8%A8,+25+main.st,+Al+Nuzhah,+Taif+26311,+Saudi+Arabia&t=&z=17&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="240"
                style={{ border: 0, display: "block" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            {/* Social */}
            <div className="mt-10">
              <p className="text-gray-500 text-sm mb-4 text-right" style={{ fontFamily: "'Cairo', sans-serif" }}>تابعنا على</p>
              <div className="flex gap-3 flex-row-reverse">
                {[
                  { icon: "ri-instagram-fill", label: "Instagram" },
                  { icon: "ri-twitter-x-fill", label: "Twitter" },
                  { icon: "ri-snapchat-fill", label: "Snapchat" },
                  { icon: "ri-tiktok-fill", label: "TikTok" },
                ].map((s) => (
                  <a
                    key={s.label}
                    href="#"
                    aria-label={s.label}
                    className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a] border border-white/10 hover:border-amber-400/40 hover:text-amber-400 text-gray-400 rounded-lg transition-all cursor-pointer"
                  >
                    <i className={`${s.icon} text-base`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-[#141414] border border-white/5 rounded-lg p-8">
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <div className="w-16 h-16 flex items-center justify-center bg-green-500/15 rounded-full">
                  <i className="ri-check-double-fill text-3xl text-green-400"></i>
                </div>
                <h4 className="text-white text-xl font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>تم الإرسال بنجاح!</h4>
                <p className="text-gray-400 text-sm text-center" style={{ fontFamily: "'Cairo', sans-serif" }}>سنتواصل معك في أقرب وقت ممكن</p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-2 text-amber-400 text-sm cursor-pointer hover:underline"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  إرسال رسالة أخرى
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5 text-right"
              >
                <h4 className="text-white font-bold text-xl mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>أرسل لنا رسالة</h4>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block" style={{ fontFamily: "'Cairo', sans-serif" }}>الاسم الكامل</label>
                  <input
                    name="name"
                    type="text"
                    required
                    placeholder="أدخل اسمك"
                    className="w-full bg-[#1e1e1e] border border-white/10 focus:border-amber-400/50 text-white text-sm px-4 py-3 rounded-lg outline-none transition-all placeholder:text-gray-600 text-right"
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block" style={{ fontFamily: "'Cairo', sans-serif" }}>البريد الإلكتروني</label>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="example@email.com"
                    className="w-full bg-[#1e1e1e] border border-white/10 focus:border-amber-400/50 text-white text-sm px-4 py-3 rounded-lg outline-none transition-all placeholder:text-gray-600 text-right"
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block" style={{ fontFamily: "'Cairo', sans-serif" }}>رقم الجوال</label>
                  <input
                    name="phone"
                    type="tel"
                    placeholder="+966 5X XXX XXXX"
                    className="w-full bg-[#1e1e1e] border border-white/10 focus:border-amber-400/50 text-white text-sm px-4 py-3 rounded-lg outline-none transition-all placeholder:text-gray-600 text-right"
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block" style={{ fontFamily: "'Cairo', sans-serif" }}>الرسالة</label>
                  <textarea
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    placeholder="اكتب رسالتك هنا..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-[#1e1e1e] border border-white/10 focus:border-amber-400/50 text-white text-sm px-4 py-3 rounded-lg outline-none transition-all placeholder:text-gray-600 resize-none text-right"
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  ></textarea>
                  <p className={`text-xs mt-1 text-left ${charCount > 450 ? "text-red-400" : "text-gray-600"}`}>
                    {charCount}/500
                  </p>
                </div>

                {status === "error" && (
                  <p className="text-red-400 text-sm text-center" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    حدث خطأ، يرجى المحاولة مجدداً
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="bg-amber-400 hover:bg-amber-500 disabled:opacity-60 text-black font-bold py-3 rounded-lg text-base transition-all whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  {status === "sending" ? "جارٍ الإرسال..." : "إرسال الرسالة"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
